import { EventEmitter } from 'events'
import { Base_Dispatcher } from '../../zap/Base/Base.Dispatcher'
import type { Form_IDisplayForm } from '../../zap/Form/Form.types'
import { UA_emptyAddUnitsData } from '../../zap/UA/UA.emptyAddUnitsData'
import { UA_readyAddUnits } from '../../zap/UA/UA.readyAddUnits'
import { UA_selectAllByType } from '../../zap/UA/UA.selectAllByTypes'
import type {
    Type_ActivityWorkflow,
    Type_DisplayUnitUuid,
    Type_IAction,
    Type_IActionType,
    Type_IDisplayUnit,
    Type_ILocation,
    Type_IReportSection,
    Type_IUnit,
    Type_IUnitList,
} from '../../zap/Type/Type.hive'
import {
    Acti_CANCEL_REPORT,
    Acti_READY_ADD_UNITS,
    Acti_SELECT_ALL_BY_TYPE,
    Acti_SET_ACTION_TYPES,
    Acti_SET_ACTIVITY,
    Acti_SET_CAMERA_FAILED,
    Acti_SET_CURRENT_ACTION,
    Acti_SET_DISPLAY_UNITS,
    Acti_SET_FORM,
    Acti_SET_FORM_TEMPLATE,
    Acti_SET_SECTIONS,
    Acti_SET_STORE,
    Acti_SET_TARGET_UNIT_TYPE,
    Acti_SET_UNIT_FORM_UNIT_ID,
} from '../../zap/Acti/Acti.constants'
import type { AppUnitType } from '../../../shared/domain/units/types.no-deps'

const CHANGE_EVENT = 'change'
let _selectedStore = null as Type_ILocation
let _activity = null as Type_ActivityWorkflow
let _actionTypes = [] as Type_IActionType[]
let _currentAction = null as Type_IAction
let _currentForm: Form_IDisplayForm = null
let _cameraFailed: boolean = false
let _sections: Type_IReportSection[] = []
let _displayUnits: Type_IDisplayUnit[] = []
let _targetUnitType: AppUnitType = null
let _displayUnitId: Type_DisplayUnitUuid = null
let _formTemplate: Form_IDisplayForm = null
// For Add Units page
let _addUnitsData = UA_emptyAddUnitsData()

class ActivityStore extends EventEmitter {
    public setSelectedStore(selectedStore: Type_ILocation) {
        _selectedStore = selectedStore
    }

    public getSelectedStore() {
        return _selectedStore
    }

    public setActivity(data: Type_ActivityWorkflow) {
        _activity = data
    }

    public getActivity() {
        return _activity
    }

    public setActionTypes(actionTypes: Type_IActionType[]) {
        _actionTypes = actionTypes
    }

    public getActionTypes() {
        return _actionTypes
    }

    public getCurrentAction() {
        return _currentAction
    }

    public setCurrentAction(action: Type_IAction) {
        _currentAction = action
    }

    // Deprecated - old naming
    public getCurrentTask() {
        return _currentAction
    }

    public hasCurrentActivity() {
        return Boolean(_activity)
    }

    public hasCurrentGuideActivity() {
        return _activity?.type === 'guide'
    }

    public cancelActivity() {
        _selectedStore = null
        _activity = null
        _actionTypes = []
        _currentAction = null
    }

    public setForm(form: Form_IDisplayForm) {
        _currentForm = form
    }

    public getForm() {
        return _currentForm
    }

    public setCameraFailed(value: boolean) {
        _cameraFailed = value
    }

    public getCameraFailed() {
        return _cameraFailed
    }

    public getSections() {
        return _sections
    }

    public setSections(sections: Type_IReportSection[]) {
        _sections = sections
    }

    public getDisplayUnits() {
        return _displayUnits
    }

    public setDisplayUnits(displayUnits: Type_IDisplayUnit[]) {
        _displayUnits = displayUnits
    }

    public getTargetUnitType() {
        return _targetUnitType
    }

    public setTargetUnitType(unitType: AppUnitType) {
        _targetUnitType = unitType

        // Also update addUnits data
        _addUnitsData.targetUnitTypeId = unitType.id
    }

    public getUnitFormUnitId() {
        return _displayUnitId
    }

    public setUnitFormUnitId(displayUnitId: Type_DisplayUnitUuid) {
        _displayUnitId = displayUnitId
    }

    public getFormTemplate() {
        return _formTemplate
    }

    public setFormTemplate(displayForm: Form_IDisplayForm) {
        _formTemplate = displayForm
    }

    public deleteStoreData() {
        _selectedStore = null
        _activity = null
        _actionTypes = []
        _currentAction = null
        _currentForm = null
        _cameraFailed = false
    }

    // Add Unit Functions
    public getAddUnitsData() {
        return _addUnitsData
    }

    public readyAddUnits(data: {
        unitListId: number
        allUnitLists: Type_IUnitList[]
        allUnits: Type_IUnit[]
        allUnitTypes: AppUnitType[]
    }) {
        _addUnitsData = UA_readyAddUnits(_addUnitsData, data)
    }

    public selectAllByType(unitId: number) {
        // Note: This mutates the _addUnitsData in place
        UA_selectAllByType(_addUnitsData, unitId)
    }

    public emitChange() {
        this.emit(CHANGE_EVENT)
    }

    public addChangeListener(callback: any) {
        this.on(CHANGE_EVENT, callback)
    }

    public removeChangeListener(callback: any) {
        this.removeListener(CHANGE_EVENT, callback)
    }
}

export type TAction =
    | { actionType: typeof Acti_SET_ACTIVITY; data: Type_ActivityWorkflow }
    | { actionType: typeof Acti_SET_ACTION_TYPES; data: Type_IActionType[] }
    | { actionType: typeof Acti_SET_CURRENT_ACTION; data: Type_IAction }
    | { actionType: typeof Acti_CANCEL_REPORT }
    | { actionType: typeof Acti_SET_STORE; data: Type_ILocation }
    | { actionType: typeof Acti_SET_FORM; data: Form_IDisplayForm }
    | { actionType: typeof Acti_SET_CAMERA_FAILED; data: boolean }
    | { actionType: typeof Acti_SET_DISPLAY_UNITS; data: Type_IDisplayUnit[] }
    | { actionType: typeof Acti_SET_TARGET_UNIT_TYPE; data: AppUnitType }
    | { actionType: typeof Acti_SET_SECTIONS; data: Type_IReportSection[] }
    | { actionType: typeof Acti_SET_UNIT_FORM_UNIT_ID; data: Type_DisplayUnitUuid }
    | { actionType: typeof Acti_SET_FORM_TEMPLATE; data: Form_IDisplayForm }
    | {
          actionType: typeof Acti_READY_ADD_UNITS
          data: {
              unitListId: number
              allUnitLists: Type_IUnitList[]
              allUnits: Type_IUnit[]
              allUnitTypes: AppUnitType[]
          }
      }
    | {
          actionType: typeof Acti_SELECT_ALL_BY_TYPE
          data: { unitId: number }
      }

const store = new ActivityStore()

// Register callback to handle all updates
Base_Dispatcher.register(async function (action: TAction) {
    if (action.actionType === undefined) {
        throw Error('Attempting to dispatch an undefined action in activity store. Please check your constants.')
    }

    // Log all actions
    console.log('Action', action.actionType, (action as any).data) // eslint-disable-line no-console

    switch (action.actionType) {
        case Acti_SET_ACTIVITY:
            store.setActivity(action.data)
            store.emitChange()
            break

        case Acti_SET_ACTION_TYPES:
            store.setActionTypes(action.data)
            break

        case Acti_SET_CURRENT_ACTION:
            store.setCurrentAction(action.data)
            store.emitChange()
            break

        case Acti_CANCEL_REPORT:
            store.cancelActivity()
            break

        case Acti_SET_STORE:
            store.setSelectedStore(action.data)
            break

        case Acti_SET_FORM:
            store.setForm(action.data)
            store.emitChange()
            break

        case Acti_SET_CAMERA_FAILED:
            store.setCameraFailed(action.data)
            store.emitChange()
            break

        case Acti_SET_DISPLAY_UNITS:
            store.setDisplayUnits(action.data)
            store.emitChange()
            break

        case Acti_SET_TARGET_UNIT_TYPE:
            store.setTargetUnitType(action.data)
            store.emitChange()
            break

        case Acti_SET_SECTIONS:
            store.setSections(action.data)
            store.emitChange()
            break

        case Acti_SET_UNIT_FORM_UNIT_ID:
            store.setUnitFormUnitId(action.data)
            store.emitChange()
            break

        case Acti_SET_FORM_TEMPLATE:
            store.setFormTemplate(action.data)
            store.emitChange()
            break

        case Acti_READY_ADD_UNITS:
            store.readyAddUnits(action.data)
            store.emitChange()
            break

        case Acti_SELECT_ALL_BY_TYPE:
            store.selectAllByType(action.data.unitId)
            store.emitChange()
            break

        default:
            const assertNever = (x: never) => x
            assertNever(action)
            return true
    }
})

export default store
